/**
 * The HTTP half of the client.
 *
 * There is no access token in the page at all: both tokens are httpOnly
 * cookies (`gamer_id`, 15 minutes; `refreshToken`, 3 days) set by
 * t-game-core's auth, so nothing that gets script into the page can read a
 * credential. The page cannot see them either, which is why the session hint
 * in `state/session.ts` exists.
 *
 * Every request goes through `request()`, so the 401 → refresh → retry dance
 * happens once, here, instead of at every call site.
 */
import type { Bracket } from '@shared/brackets';
import type { TourStep } from '@shared/harbour-tour';
import type { MatchMode } from '@shared/modes';
import type { NotificationsView } from '@shared/notifications';
import type { ShipClassId } from '@shared/ship-spec';

import { announceDesertion } from '@/state/desertion';

const BASE = import.meta.env.VITE_API_URL || '';

export interface PublicPlayer {
  id: string;
  nickname: string;
  email: string | null;
  /**
   * Whether the address has been confirmed. On the session record because it
   * decides which screen the app is: `App.vue` renders nothing but the
   * check-your-inbox gate while it is false.
   */
  emailVerified: boolean;
  /**
   * Whether to offer this account the admin panel. A signal to draw a link
   * with, never an authorisation — every `/api/admin/*` route is behind
   * `AdminAccessGuard` regardless of what the client believes.
   */
  isAdmin: boolean;
  credits: number;
  diamonds: number;
  createdAt: string;
}

/** The template's account row, as t-game-core's auth serves it. */
interface TemplateUser {
  id: string;
  email: string;
  username: string | null;
  role: string;
  emailVerified: boolean;
}

/**
 * Balances are zero here: the template's auth knows nothing about the game
 * economy. A game screen that shows a wallet fetches it and calls
 * `useSession().setBalances`.
 */
function toPlayer(user: TemplateUser): PublicPlayer {
  return {
    id: user.id,
    nickname: user.username ?? user.email.split('@')[0] ?? 'sailor',
    email: user.email,
    emailVerified: user.emailVerified,
    isAdmin: user.role === 'ADMIN',
    credits: 0,
    diamonds: 0,
    createdAt: '',
  };
}

export interface SessionResponse {
  player: PublicPlayer;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** From a 429's `Retry-After` header, in seconds; undefined when the server did not say. */
    readonly retryAfterS?: number,
    /** The server's machine-readable `code` (CONFIRM_REQUIRED, BALANCE_NEGATIVE, ...) when the body carries one. */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Account shapes ──────────────────────────────────────────────────────────

/** `GET`/`PATCH /api/account`. */
export interface AccountProfile {
  id: string;
  email: string;
  emailVerified: boolean;
  /** The name on an invoice. Null is ordinary, not an error. */
  fullName: string | null;
  /** The player's call sign, shown to other players. */
  callSign: string | null;
  locale: string | null;
  currency: string | null;
  createdAt: string;
}

/** `GET /api/battle/me`. */
export interface BattleSummary {
  stats: { battles: number; wins: number; kills: number; damage: number; xp: number };
  lastBattle: {
    id: string;
    endedAt: string;
    durationS: number;
    won: boolean;
    draw: boolean;
    specId: string;
    damage: number;
    kills: number;
    survived: boolean;
    credits: number;
    xp: number;
  } | null;
  wallet: { credits: number; diamonds: number };
  /** The bracket this player's hull sails in (`shared/brackets.ts`). */
  bracket: Bracket;
  /** The mode every arena is pinned to on this server, or null when captains choose. */
  pinnedMode: MatchMode | 'rotate' | null;
  /** Every arena open right now, with who is in it. */
  arenas: {
    mode: MatchMode | 'rotate';
    bracket: Bracket;
    phase: 'waiting' | 'countdown' | 'battle' | 'results';
    humans: number;
  }[];
}

// ─── Progression ─────────────────────────────────────────────────────────────

/** One daily or weekly order, as `GET /api/progression` reports it. */
export interface QuestView {
  /** Address a claim to this, not to the slug. */
  id: string;
  slug: string;
  /** Translation keys — the server never sends prose. See `src/i18n`. */
  nameKey: string;
  descriptionKey: string;
  type: 'daily' | 'weekly';
  progress: number;
  target: number;
  status: 'ACTIVE' | 'COMPLETED' | 'CLAIMED' | 'EXPIRED';
  claimable: boolean;
  reward: ClaimPayout;
}

/** What an order or a task pays. `diamonds` are seals; `passXp` is the season-pass XP the claim also earns. */
export interface ClaimPayout {
  credits: number;
  diamonds: number;
  xp: number;
  passXp: number;
}

/** One monthly, super or global task, as `GET /api/progression/tasks` reports it. */
export interface TaskView {
  /** Address a claim to this (the same route as an order's). */
  id: string;
  slug: string;
  /** Translation keys; the description takes `{target}`. */
  nameKey: string;
  descriptionKey: string;
  period: 'monthly' | 'super' | 'global';
  /** Own count; for a global task the community total, held at the target. */
  progress: number;
  target: number;
  status: 'ACTIVE' | 'COMPLETED' | 'CLAIMED';
  claimable: boolean;
  /** A finished task from an earlier period, still waiting to be claimed. */
  carriedOver: boolean;
  /** When this period ends; null for a super task and a carried-over row. */
  endsAt: string | null;
  reward: ClaimPayout;
  /** Global tasks only: the captain's own share and who takes part. */
  community: { contribution: number; minContribution: number; participants: number; reached: boolean } | null;
}

export interface TasksOverview {
  monthly: TaskView[];
  super: TaskView[];
  global: TaskView[];
  monthlyResetsAt: string;
}

/** What one side of one pass tier pays; any mix of the fields. */
export interface PassReward {
  credits?: number;
  seals?: number;
  livery?: string;
  flag?: string;
}

export type PassRewardState = 'locked' | 'claimable' | 'claimed' | 'needs_premium';
export type PassTrack = 'free' | 'premium';

export interface PassTierView {
  tier: number;
  /** Pass XP at which the tier opens. */
  xp: number;
  free: PassReward;
  premium: PassReward;
  freeState: PassRewardState;
  premiumState: PassRewardState;
}

export interface PassSeasonView {
  id: string;
  /** English name; the pass screen translates `pass.season.<id>` first. */
  name: string;
  startsAt: string;
  endsAt: string;
  xp: number;
  currentTier: number;
  premium: boolean;
  premiumPriceSeals: number;
  /** A spend of this many seals or more needs `confirm: true`. */
  confirmAtSeals: number;
  tiers: PassTierView[];
}

/** `GET /api/pass`: `pass` is null when no season is running. */
export interface PassView {
  pass: PassSeasonView | null;
}

/** What a claim or the premium purchase answers: the pass and the new balances. */
export interface PassMutationResponse extends PassView {
  wallet: { credits: number; diamonds: number };
}

// ─── Calendar and achievements ───────────────────────────────────────────────

/** What a calendar day or an achievement tier pays (`engagement/reward.ts`). */
export interface EngagementReward {
  credits?: number;
  diamonds?: number;
  xp?: number;
  /** An unlock-only livery id. */
  liveryId?: string;
}

/** `GET /api/calendar`. */
export interface CalendarView {
  cycle: number;
  claimedDays: number;
  claimableToday: boolean;
  nextDay: number;
  /** When today's lock opens (ISO), or null while today is claimable. */
  nextClaimAt: string | null;
  days: { day: number; reward: EngagementReward; claimed: boolean }[];
}

export interface CalendarClaim {
  calendar: CalendarView;
  day: number;
  reward: EngagementReward;
}

export interface AchievementView {
  /** Translation key `achievements.names.<id>`. */
  id: string;
  /** Translation key `achievements.stats.<stat>`. */
  stat: string;
  value: number;
  claimedTier: number;
  tiers: { tier: number; target: number; reward: EngagementReward; reached: boolean; claimed: boolean }[];
  claimable: boolean;
}

export interface AchievementClaim {
  achievement: AchievementView;
  tier: number;
  reward: EngagementReward;
}

export interface LeaderboardView {
  key: string;
  /** Translation key — `leaderboard.<key>`. */
  labelKey: string;
  /** The country board's country (ISO alpha-2); null elsewhere, and on the country board while the captain's own is unknown. */
  country: string | null;
  rows: LeaderboardRow[];
  me: {
    rank: number;
    value: number;
    total: number;
    row: LeaderboardRow;
    /** Neighbours of an own row that is outside `rows`; empty when it is inside. */
    above: LeaderboardRow[];
    below: LeaderboardRow[];
  } | null;
}

export interface LeaderboardRow {
  /** Position on the board, 1-based. */
  rank: number;
  userId: string;
  name: string;
  value: number;
  navalRank: { id: string; index: number; nameKey: string; abbrKey: string };
  wins: number;
  battles: number;
  /** 0..1. */
  winRate: number;
  /** Catalogue hull id, e.g. `cruiser_t3`. */
  hullId: string;
  liveryId: string;
  flagId: string;
  you: boolean;
}

/** A walkout the captain has not been told about: the unseen ones, summed. */
export interface DesertionNotice {
  /** Acknowledge with this. */
  id: string;
  ratingLost: number;
  creditsFined: number;
  count: number;
  at: string;
}

/**
 * `GET /api/progression`. `rank` is derived from `xp` by `@shared/ranks` on the
 * server; the client recomputes it from `xp` wherever it needs a live figure,
 * so this copy is only what the server saw at request time.
 */
export interface ProgressionOverview {
  /** `id` names a rung; `rankNameKey(id)` turns it into a translation key. */
  rank: { index: number; id: string; minXp: number; nextXp: number | null; progress: number };
  xp: number;
  stats: { battles: number; wins: number; kills: number; damage: number; xp: number };
  season: { nameKey: string; number: number; endsAt: string; points: number; tierKey: string | null } | null;
  quests: QuestView[];
  dailyDone: number;
  dailyTotal: number;
  /** Finished monthly, super and global tasks not yet claimed (the harbour's badge adds them to the orders'). */
  tasksClaimable: number;
  /** Each hull the captain has sailed, with its own research XP, richest first. */
  ships: { hullId: string; xp: number; elite: boolean }[];
  /** Fined for leaving a battle and not yet told; the client pins a slip once. */
  desertion: DesertionNotice | null;
}

/** `GET /api/tutorial`: progress through the harbour tour (`@shared/harbour-tour`). */
export interface TutorialView {
  step: TourStep;
  stepIndex: number;
  steps: readonly TourStep[];
  completed: TourStep[];
  skipped: boolean;
  finished: boolean;
  /** The battle lesson has been done or skipped. */
  trainingDone: boolean;
  replays: number;
}

/** `GET /api/account/restart/preview`; silver figures, the screen words them. */
export interface RestartPreview {
  liquidated: number;
  returned: number;
  startingCredits: number;
  returnPct: number;
  /** Translation-key segments, `papers.restart.keeps.<key>`. */
  keeps: string[];
  confirmTtlMin: number;
  cooldownDays: number;
  /** ISO time the next restart is allowed, or null when one is allowed now. */
  availableAt: string | null;
}

export interface RestartRequested {
  sent: true;
  expiresAt: string;
  preview: RestartPreview;
  /** Present off production only, where no mailer delivers the link. */
  devConfirmUrl?: string;
}

export interface RestartConfirmed {
  restarted: true;
  preview: RestartPreview;
  wallet: { credits: number; diamonds: number };
}

/** `GET`/`PATCH /api/me/preferences` — the template's. */
export interface AccountPreferences {
  locale: string | null;
  currency: string | null;
}

/** `GET /api/i18n/config` — the template's, and the only source of the two dropdowns' options. */
export interface I18nConfig {
  locales: { code: string; tag: string; label: string }[];
  defaultLocale: string;
  currencies: { code: string; symbol: string }[];
  defaultCurrency: string;
}

/** One row of `GET /api/invoices`, the template's. */
export interface InvoiceRow {
  id: string;
  number: string;
  type: string;
  amountCents: number;
  vatCents: number;
  totalCents: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
}

/** One row of `GET /api/account/purchases`. */
export interface PurchaseRow {
  id: string;
  kind: 'diamonds' | 'credits' | 'other';
  currencyType: string;
  /** Signed: negative for a spend, so the list reads as a statement. */
  amount: number;
  balanceAfter: number;
  description: string;
  reference: string | null;
  /** Present when the movement was billed — drives the PDF link. */
  invoiceNumber: string | null;
  at: string;
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

/** A legal document — `GET /api/cms/legal/:slug`, the template's. */
export interface LegalDocument {
  slug: string;
  title: string;
  content: string;
  updatedAt: string | null;
}

// ─── Shipyard ────────────────────────────────────────────────────────────────

export type ShipyardItemKind = 'HULL' | 'TURRET' | 'MODULE';

export interface ShipyardItem {
  /** `itemNameKey(item)` / `itemBlurbKey(item)` name it — see `@shared/catalogue-keys`. */
  id: string;
  kind: ShipyardItemKind;
  tier: number;
  classId: ShipClassId | null;
  priceCredits: number;
  priceDiamonds: number | null;
  premium: boolean;
  stats: Record<string, unknown>;
  modelId: string | null;
  owned: boolean;
  level: number;
  nextUpgradeCredits: number | null;
  researchNodeId: string | null;
  buyable: boolean;
}

export interface ShipyardLoadout {
  hullId: string;
  turretIds: string[];
  moduleIds: string[];
}

/** A node in one hull's own research bush (GDD progression.md §9). */
export type ResearchNodeKind = 'gun' | 'hull' | 'propulsion' | 'fire_control' | 'torpedoes' | 'next_hull' | 'line_opener';

export interface ResearchNode {
  id: string;
  kind: ResearchNodeKind;
  /** A turret catalogue id (gun), or the unlocked hull id (next_hull/line_opener); null otherwise. */
  grantsItemId: string | null;
  statDelta: Record<string, number> | null;
  xpCost: number;
  creditCost: number;
  /** Other node ids, same hull's bush (or the opening hull's node, for a hull-unlock kind). */
  prereqs: string[];
  state: 'locked' | 'available' | 'researched';
  /** Whether the wallet and this hull's own XP currently cover the cost. */
  affordable: boolean;
}

/** One hull's bush: its own XP, whether every non-unlock node is done, and its nodes. */
export interface HullResearch {
  hullId: string;
  xp: number;
  elite: boolean;
  nodes: ResearchNode[];
}

/** `ShipyardOverview.research` — replaces the old class-wide, timed research tree. */
export interface ResearchOverview {
  /** `CommonResearchPool.xp` — fed automatically once a hull is elite. */
  pool: number;
  hulls: HullResearch[];
}

export type CosmeticKind = 'LIVERY' | 'FLAG';

/** A paint job on the shop shelf. Colours are `0xRRGGBB`, from `@shared/liveries`. */
export interface ShipyardLivery {
  id: string;
  name: string;
  blurb: string;
  priceDiamonds: number | null;
  premium: boolean;
  unlockOnly: boolean;
  base: number;
  stripe: number;
  trim: number;
  pattern: 'plain' | 'dazzle' | 'banded' | 'splinter';
  metal: number;
  owned: boolean;
  equipped: boolean;
}

export interface ShipyardFlag {
  id: string;
  name: string;
  priceDiamonds: number | null;
  premium: boolean;
  unlockOnly: boolean;
  layout: 'bars' | 'vbars' | 'cross' | 'saltire' | 'canton' | 'quarters';
  colors: number[];
  owned: boolean;
  equipped: boolean;
}

export interface ShipyardCosmetics {
  liveries: ShipyardLivery[];
  flags: ShipyardFlag[];
  shipName: string | null;
  /** Diamonds the next rename costs; zero while the first one is still free. */
  renamePriceDiamonds: number;
  shipNameMin: number;
  shipNameMax: number;
}

/** `GET /api/shipyard` — and what every shipyard write answers with. */
export interface ShipyardOverview {
  wallet: { credits: number; diamonds: number };
  activeHullId: string;
  stockIds: string[];
  maxModules: number;
  maxLevel: number;
  items: ShipyardItem[];
  loadouts: ShipyardLoadout[];
  research: ResearchOverview;
  cosmetics: ShipyardCosmetics;
}

// ─── Boosters and premium ────────────────────────────────────────────────────
// Mirrors backend/src/game/boosters/boosters.types.ts one for one.

export type BoosterTier = 'bronze' | 'silver' | 'gold';
export type BoostKind = 'CREDITS' | 'XP';

export interface BoosterSkuView {
  /** `credit_bronze` ... `xp_gold`. */
  id: string;
  kind: BoostKind;
  tier: BoosterTier;
  /** Total multiplier this tier gives on its own: 1.5, 2 or 3. */
  multiplier: number;
  /** Battles one pack covers. */
  battles: number;
  priceSeals: number;
  pricePerBattleSeals: number;
  /** "from EUR x" at the shop's best seal rate, EUR cents. Display only: EUR is never charged for a booster. */
  eurFromCents: number;
  /** A spend this size needs `confirm: true`. */
  needsConfirm: boolean;
}

export interface PremiumSkuView {
  days: number;
  priceSeals: number;
  eurFromCents: number;
  needsConfirm: boolean;
}

export interface OwnedBoosterView {
  sku: string;
  kind: BoostKind;
  tier: BoosterTier;
  multiplier: number;
  battlesLeft: number;
}

export interface SpendStatus {
  windowDays: number;
  eurCents: number;
  softLimitEurCents: number;
  reviewLimitEurCents: number;
  soft: boolean;
  blocked: boolean;
}

export interface BoostersOverview {
  /** Premium balance; negative after a refund or chargeback, when spending is refused. */
  seals: number;
  packBattles: number;
  confirmAtSeals: number;
  /** The ceiling of any multiplier, x3.5. */
  multiplierCap: number;
  skus: BoosterSkuView[];
  premiumSkus: PremiumSkuView[];
  premium: { active: boolean; until: string | null; bonus: number };
  /** Stacks with charges left, strongest first. */
  owned: OwnedBoosterView[];
  /** What the next battle would pay. */
  next: { creditMultiplier: number; xpMultiplier: number; creditSku: string | null; xpSku: string | null };
  spend: SpendStatus;
}

// ─── Market ──────────────────────────────────────────────────────────────────
// Mirrors backend/src/game/market/market.types.ts one for one.

export type MarketKind = 'hull' | 'fitting' | 'livery' | 'flag' | 'booster';

/** Why a held item cannot be sold right now; each has a sentence under `shipyard.sell.why`. */
export type SaleReason = 'not_sold_for_seals' | 'active_ship' | 'equipped' | 'fitted' | 'none_left' | 'worthless';

export interface SaleQuote {
  sellable: boolean;
  reason: SaleReason | null;
  /** Seals one unit is valued at before commission. */
  unitValue: number;
  /** Most units one sale may take (1 except for a booster stack). */
  maxQuantity: number;
}

export interface MarketItemView {
  kind: MarketKind;
  itemId: string;
  /** Units held: 1, or the charges left in a booster stack. */
  held: number;
  quote: SaleQuote;
}

export interface ListingView {
  listingId: string;
  kind: MarketKind;
  itemId: string;
  quantity: number;
  mode: 'instant' | 'delayed';
  status: 'listed' | 'settled' | 'cancelled';
  unitValue: number;
  payout: number;
  createdAt: string;
  settlesAt: string;
  settledAt: string | null;
}

export interface MarketRules {
  instantPayoutPct: number;
  delayedPayoutPct: number;
  delayHours: number;
}

/** `GET /api/market`, and what a cancel answers with. */
export interface MarketOverview {
  serverTime: string;
  rules: MarketRules;
  items: MarketItemView[];
  listings: ListingView[];
}

export interface SellRequest {
  kind: MarketKind;
  itemId: string;
  quantity: number;
  mode: 'instant' | 'delayed';
  idempotencyKey: string;
}

export interface SellResult {
  listing: ListingView;
  replayed: boolean;
  overview: MarketOverview;
}

/** `GET /api/shipyard/free-xp`. */
export interface FreeXpOverview {
  perSeal: number;
  dailyCap: number;
  boughtToday: number;
  remainingToday: number;
}

/** Body of every seal spend: the key makes a retry safe, `confirm` says the captain was asked. */
export interface SpendOptions {
  idempotencyKey: string;
  confirm?: boolean;
}

/** `GET /api/exchange`: the seals-to-silver exchange's terms and what is left of today's cap. */
export interface ExchangeInfo {
  /** False while the operator keeps the exchange closed: the shop shows no button. */
  enabled: boolean;
  feePct: number;
  /** Net silver per seal after the fee. */
  silverPerSeal: number;
  /** Silver per seal before the fee (`exchange-math.ts` works out any amount from it). */
  grossPerSeal: number;
  maxSeals: number;
  /** A spend of this many seals or more must be sent with `confirm: true`. */
  confirmAtSeals: number;
  dailySilver: number;
  usedToday: number;
  remainingToday: number;
  resetsAt: string;
  wallet: { credits: number; diamonds: number };
}

/** `GET /api/exchange/quote?seals=`. */
export interface ExchangeQuote {
  seals: number;
  silver: number;
  fee: number;
  silverPerSeal: number;
  remainingToday: number;
}

export interface ExchangeRecord {
  id: string;
  seals: number;
  silver: number;
  fee: number;
  silverPerSeal: number;
  createdAt: string;
}

/** `POST /api/exchange`. */
export interface ExchangeResult {
  exchange: ExchangeRecord;
  replayed: boolean;
  wallet: { credits: number; diamonds: number };
}

/** Why a rewarded ad is not on offer (`backend/src/game/shop/ads.data.ts`). */
export type RewardedBlock = 'provider' | 'disabled' | 'cap' | 'interval';

/** `GET /api/ads/rewarded`. */
export interface RewardedStatus {
  available: boolean;
  block: RewardedBlock | null;
  remainingToday: number;
  /** When the interval lets the next view in. */
  nextAt: string | null;
  reward: { credits: number };
}

/** `POST /api/ads/rewarded/claim`. */
export interface RewardedClaim {
  replayed: boolean;
  reward: { credits: number };
  remainingToday: number;
  wallet: { credits: number; diamonds: number };
}

/** `GET /api/topup/packages` — the diamond packs, priced in the player's currency. */
export interface TopupPackage {
  id: string;
  name: string;
  blurb: string;
  priceCents: number;
  diamonds: number;
  bonusDiamonds: number;
  total: number;
  highlight: boolean;
}

/**
 * Any euro amount at the base rate. Everything here is **EUR** cents, whatever
 * `TopupPackages.currency` is: the player types euros (the catalogue's base) and
 * `TopupPackages.rate` turns them into the charge currency for display.
 */
export interface TopupCustomTerms {
  minCents: number;
  maxCents: number;
  /** Price of one hundred seals, in EUR cents. */
  rateCentsPer100: number;
}

export interface TopupPackages {
  packages: TopupPackage[];
  /** The live deals: sold by the same deposit call as a pack, under their own id. */
  deals: TopupPackage[];
  /** The currency every pack price is in, and the one the player will be charged in. */
  currency: string;
  /** `EUR -> currency`; 1 when the answer is in euros. */
  rate: number;
  custom: TopupCustomTerms;
}

// ─── Offers ──────────────────────────────────────────────────────────────────

/** Where outside the shop a deal may show (`backend/src/game/offers/offers.data.ts`). */
export type OfferPlacement = 'login' | 'after_battle' | 'low_credits' | 'chandler';

/** A deal as `GET /api/offers/:placement` serves it. `until` is an ISO instant the client counts down to. */
export interface Offer {
  id: string;
  name: string;
  blurb: string;
  priceCents: number;
  currency: string;
  diamonds: number;
  bonus: number;
  total: number;
  from: string;
  until: string;
  highlight: boolean;
}

/** `show: false` carries an empty `offers`: draw nothing. */
export interface OfferDecision {
  placement: OfferPlacement;
  show: boolean;
  offers: Offer[];
}

/** `GET /api/news` — a page of notices. Bodies come with `newsArticle`. */
export interface NewsSummary {
  id: string;
  slug: string;
  title: string;
  summary: string;
  imageUrl: string | null;
  tag: string;
  publishedAt: string | null;
  createdAt: string;
}

export interface NewsPage {
  featured: NewsSummary[];
  items: NewsSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export interface NewsArticle extends NewsSummary {
  content: string;
}

/** `GET /api/cms/faq` — the template's. */
export interface FaqEntry {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
}

// ─── Transport ───────────────────────────────────────────────────────────────

/** Deduplicates concurrent refreshes — several 401s must not race each other. */
let refreshing: Promise<boolean> | null = null;

/**
 * Exported for `admin/admin-api.ts`, which is a second API surface that wants
 * the same cookie, the same 401-refresh interceptor and the same `ApiError`.
 */
export async function request<T>(
  path: string,
  init: RequestInit = {},
  retryOn401 = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    // Needed for the refresh cookie to be sent and set.
    credentials: 'include',
  });

  if (response.status === 401 && retryOn401 && path !== '/api/auth/refresh') {
    if (await refreshSession()) return request<T>(path, init, false);
  }

  if (!response.ok) {
    const failure = await readError(response);
    throw new ApiError(failure.message, response.status, readRetryAfter(response), failure.code);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** `Retry-After` as whole seconds; the HTTP-date form is not used by the throttler. */
function readRetryAfter(response: Response): number | undefined {
  const s = Number(response.headers.get('Retry-After'));
  return response.status === 429 && Number.isFinite(s) && s > 0 ? Math.ceil(s) : undefined;
}

async function readError(response: Response): Promise<{ message: string; code?: string }> {
  const fallback = response.status >= 500 ? 'The server had a problem.' : 'That request was refused.';
  try {
    const body = (await response.json()) as { message?: unknown; code?: unknown };
    const code = typeof body.code === 'string' ? body.code : undefined;
    // class-validator returns an array of messages; the first is enough.
    if (Array.isArray(body.message)) return { message: String(body.message[0]), code };
    if (typeof body.message === 'string') return { message: body.message, code };
    return { message: fallback, code };
  } catch {
    // Fall through to the generic message below.
  }
  return { message: fallback };
}

/** True if the session cookies were rotated successfully. */
export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    try {
      const { user } = await request<{ user: TemplateUser }>(
        '/api/auth/refresh',
        { method: 'POST' },
        false,
      );
      onRefreshed?.({ player: toPlayer(user) });
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

/**
 * Notified whenever a refresh succeeds, so the session store can keep the
 * player record current without this module importing Vue.
 */
let onRefreshed: ((session: SessionResponse) => void) | null = null;
export function onSessionRefreshed(handler: (session: SessionResponse) => void): void {
  onRefreshed = handler;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export const api = {
  /**
   * `altcha` is the solved proof-of-work payload from the ALTCHA widget — the
   * template requires one on register and login. The call sign travels as the
   * template's `username`.
   */
  async register(body: { username: string; email: string; password: string; altcha: string }) {
    const { user } = await request<{ user: TemplateUser }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { player: toPlayer(user) } satisfies SessionResponse;
  },

  /**
   * Records the ticked terms checkbox for the player who has just registered
   * (`POST /api/consent/terms`): the server stamps its own legal versions.
   */
  acceptTerms() {
    return request<{ termsVersion: string; privacyVersion: string; acceptedAt: string }>('/api/consent/terms', {
      method: 'POST',
      body: JSON.stringify({ accepted: true }),
    });
  },

  async login(body: { email: string; password: string; altcha: string }) {
    const { user } = await request<{ user: TemplateUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { player: toPlayer(user) } satisfies SessionResponse;
  },

  logout() {
    return request<void>('/api/auth/logout', { method: 'POST' });
  },

  async me() {
    const { user } = await request<{ user: TemplateUser }>('/api/auth/me');
    return { player: toPlayer(user) };
  },

  // ─── Account ───────────────────────────────────────────────────────────────

  account() {
    return request<AccountProfile>('/api/account');
  },

  updateAccount(fullName: string) {
    return request<AccountProfile>('/api/account', {
      method: 'PATCH',
      body: JSON.stringify({ fullName }),
    });
  },

  updateCallSign(callSign: string) {
    return request<AccountProfile>('/api/account/callsign', {
      method: 'PATCH',
      body: JSON.stringify({ callSign }),
    });
  },

  changePassword(currentPassword: string, newPassword: string) {
    return request<void>('/api/account/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  resendVerification() {
    return request<{ sent: boolean; devVerifyUrl?: string }>(
      '/api/account/resend-verification',
      { method: 'POST' },
    );
  },

  verifyEmail(token: string) {
    return request<{ verified: boolean }>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  welcome() {
    return request<{ welcomed: boolean }>('/api/account/welcome', { method: 'POST' });
  },

  forgotPassword(email: string, altcha: string) {
    return request<{ queued: boolean }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, altcha }),
    });
  },

  resetPassword(token: string, newPassword: string, altcha: string) {
    return request<{ reset: boolean }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword, altcha }),
    });
  },

  preferences() {
    return request<AccountPreferences>('/api/me/preferences');
  },

  updatePreferences(body: { locale?: string; currency?: string }) {
    return request<AccountPreferences>('/api/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  i18nConfig() {
    return request<I18nConfig>('/api/i18n/config');
  },

  invoices(page = 1) {
    return request<Paged<InvoiceRow>>(`/api/invoices?page=${page}`);
  },

  invoicePdfUrl(number: string) {
    return `${BASE}/api/invoices/${encodeURIComponent(number)}/download`;
  },

  purchases(page = 1, kind: 'all' | 'diamonds' | 'credits' = 'all') {
    return request<Paged<PurchaseRow>>(`/api/account/purchases?page=${page}&kind=${kind}`);
  },

  deleteAccount(password: string, confirmEmail: string) {
    return request<{ deleted: true; invoicesArchived: number }>('/api/account', {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmEmail }),
    });
  },

  // ─── Account restart ───────────────────────────────────────────────────────

  /** `GET /api/account/restart/preview` — what a restart sells and pays back, and when the next is allowed. */
  restartPreview() {
    return request<RestartPreview>('/api/account/restart/preview');
  },

  /** Mails the one-time link; nothing is sold until it is opened. */
  requestRestart() {
    return request<RestartRequested>('/api/account/restart/request', { method: 'POST' });
  },

  /** Spends the e-mailed token and restarts the account. */
  confirmRestart(token: string) {
    return request<RestartConfirmed>('/api/account/restart/confirm', { method: 'POST', body: JSON.stringify({ token }) });
  },

  legalDocument(slug: string) {
    return request<LegalDocument>(`/api/cms/legal/${encodeURIComponent(slug)}`);
  },

  // ─── Battle ────────────────────────────────────────────────────────────────

  /** `GET /api/battle/me` — lifetime record, last battle, wallet, bracket and the arenas. */
  battleMe() {
    return request<BattleSummary>('/api/battle/me');
  },

  // ─── Progression ───────────────────────────────────────────────────────────

  /** `GET /api/progression` — rank, record, season and the day's orders in one. */
  progression() {
    return request<ProgressionOverview>('/api/progression').then((overview) => {
      announceDesertion(overview.desertion, (id) => api.ackDesertion(id));
      return overview;
    });
  },

  /** The desertion slip has been shown; the server stops reporting it. */
  ackDesertion(id: string) {
    return request<{ ok: true }>('/api/progression/desertion/seen', { method: 'POST', body: JSON.stringify({ id }) });
  },

  // ─── Tutorial (the harbour tour) ───────────────────────────────────────────

  /** `GET /api/tutorial` — the tour's server-side progress; created on first read. */
  tutorial() {
    return request<TutorialView>('/api/tutorial');
  },

  /** Completes the current step (the server checks order, and that the captain has been to sea for `sortie`). */
  completeTutorialStep(step: TourStep) {
    return request<TutorialView>(`/api/tutorial/steps/${step}/complete`, { method: 'POST' });
  },

  /** The battle lesson ended or was skipped. */
  tutorialTrainingDone() {
    return request<TutorialView>('/api/tutorial/training/done', { method: 'POST' });
  },

  skipTutorial() {
    return request<TutorialView>('/api/tutorial/skip', { method: 'POST' });
  },

  replayTutorial() {
    return request<TutorialView>('/api/tutorial/replay', { method: 'POST' });
  },

  /** Just the orders, for a cheap re-read after a battle. */
  progressionQuests() {
    return request<QuestView[]>('/api/progression/quests');
  },

  /** Monthly, super and global tasks. */
  progressionTasks() {
    return request<TasksOverview>('/api/progression/tasks');
  },

  /** Claims an order or a task: both are addressed by id (`questId` is the field's old name). */
  claimQuest(questId: string) {
    return request<ClaimPayout>('/api/progression/quests/claim', {
      method: 'POST',
      body: JSON.stringify({ questId }),
    });
  },

  calendar() {
    return request<CalendarView>('/api/calendar');
  },

  claimCalendar() {
    return request<CalendarClaim>('/api/calendar/claim', { method: 'POST' });
  },

  achievements() {
    return request<{ items: AchievementView[] }>('/api/achievements');
  },

  /** Claims tier `tier`; naming it keeps a retried request from paying the next one. */
  claimAchievement(id: string, tier: number) {
    return request<AchievementClaim>(`/api/achievements/${encodeURIComponent(id)}/claim`, {
      method: 'POST',
      body: JSON.stringify({ tier }),
    });
  },

  leaderboard(category: string, limit = 100) {
    return request<LeaderboardView>(
      `/api/progression/leaderboard?category=${encodeURIComponent(category)}&limit=${limit}`,
    );
  },

  /** `GET /api/progression/notifications` — the inbox, newest first, with the unread count. */
  notifications(limit = 50) {
    return request<NotificationsView>(`/api/progression/notifications?limit=${limit}`);
  },

  /** Marks entries read (all visible ones when `ids` is omitted); answers the unread count left. */
  readNotifications(ids?: string[]) {
    return request<{ unread: number }>('/api/progression/notifications/read', {
      method: 'POST',
      body: JSON.stringify(ids ? { ids } : {}),
    });
  },

  // ─── Season pass ───────────────────────────────────────────────────────────

  pass() {
    return request<PassView>('/api/pass');
  },

  buyPassPremium(options: SpendOptions) {
    return request<PassMutationResponse>('/api/pass/premium', { method: 'POST', body: JSON.stringify(options) });
  },

  claimPass(tier: number, track: PassTrack) {
    return request<PassMutationResponse>('/api/pass/claim', { method: 'POST', body: JSON.stringify({ tier, track }) });
  },

  // ─── Boosters, premium and Free XP ─────────────────────────────────────────

  boosters() {
    return request<BoostersOverview>('/api/boosters');
  },

  buyBooster(sku: string, options: SpendOptions) {
    return request<BoostersOverview>(`/api/boosters/${encodeURIComponent(sku)}/buy`, { method: 'POST', body: JSON.stringify(options) });
  },

  buyPremium(days: number, options: SpendOptions) {
    return request<BoostersOverview>('/api/boosters/premium/buy', { method: 'POST', body: JSON.stringify({ days, ...options }) });
  },

  // ─── Rewarded ads ──────────────────────────────────────────────────────────

  rewardedStatus() {
    return request<RewardedStatus>('/api/ads/rewarded');
  },

  /** Sends the completion token the ad SDK produced; the server verifies it with the network and pays once. */
  claimRewardedAd(token: string) {
    return request<RewardedClaim>('/api/ads/rewarded/claim', { method: 'POST', body: JSON.stringify({ token }) });
  },

  // ─── Exchange (seals into silver) ──────────────────────────────────────────

  exchangeInfo() {
    return request<ExchangeInfo>('/api/exchange');
  },

  exchangeQuote(seals: number) {
    return request<ExchangeQuote>(`/api/exchange/quote?seals=${seals}`);
  },

  exchange(seals: number, options: SpendOptions) {
    return request<ExchangeResult>('/api/exchange', { method: 'POST', body: JSON.stringify({ seals, ...options }) });
  },

  // ─── Market ────────────────────────────────────────────────────────────────

  market() {
    return request<MarketOverview>('/api/market');
  },

  marketSell(body: SellRequest) {
    return request<SellResult>('/api/market/sell', { method: 'POST', body: JSON.stringify(body) });
  },

  marketCancel(listingId: string) {
    return request<MarketOverview>(`/api/market/listings/${encodeURIComponent(listingId)}/cancel`, { method: 'POST' });
  },

  freeXp() {
    return request<FreeXpOverview>('/api/shipyard/free-xp');
  },

  buyFreeXp(seals: number, options: SpendOptions) {
    return request<ShipyardOverview>('/api/shipyard/free-xp', { method: 'POST', body: JSON.stringify({ seals, ...options }) });
  },

  // ─── Shipyard ──────────────────────────────────────────────────────────────

  shipyard() {
    return request<ShipyardOverview>('/api/shipyard');
  },

  shipyardBuy(itemId: string) {
    return request<ShipyardOverview>('/api/shipyard/buy', { method: 'POST', body: JSON.stringify({ itemId }) });
  },

  shipyardEquip(body: { hullId: string; mount?: number; turretId?: string; moduleIds?: string[] }) {
    return request<ShipyardOverview>('/api/shipyard/equip', { method: 'POST', body: JSON.stringify(body) });
  },

  shipyardSelect(hullId: string) {
    return request<ShipyardOverview>('/api/shipyard/select', { method: 'POST', body: JSON.stringify({ hullId }) });
  },

  /** Buy a research node outright: instant, paid in this hull's own XP plus credits. No timer. */
  researchBuy(nodeId: string) {
    return request<ShipyardOverview>('/api/shipyard/research/buy', { method: 'POST', body: JSON.stringify({ nodeId }) });
  },

  /** Move XP into `toHullId`, paid in Seals; `fromHullId` moves it out of a hull's own stash, omitted draws on the common pool. */
  researchConvert(body: { toHullId: string; xp: number; fromHullId?: string }) {
    return request<ShipyardOverview>('/api/shipyard/research/convert', { method: 'POST', body: JSON.stringify(body) });
  },

  cosmeticBuy(kind: CosmeticKind, refId: string) {
    return request<ShipyardOverview>('/api/shipyard/cosmetic/buy', { method: 'POST', body: JSON.stringify({ kind, refId }) });
  },

  cosmeticEquip(kind: CosmeticKind, refId: string) {
    return request<ShipyardOverview>('/api/shipyard/cosmetic/equip', { method: 'POST', body: JSON.stringify({ kind, refId }) });
  },

  renameShip(shipName: string) {
    return request<ShipyardOverview>('/api/shipyard/rename', { method: 'POST', body: JSON.stringify({ shipName }) });
  },

  /** `currency`: what the payment picker has chosen; without it the player's own currency applies. */
  topupPackages(currency?: string) {
    return request<TopupPackages>(`/api/topup/packages${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`);
  },

  /** Counts as an impression on the server: call once per placement per session moment (`state/offers.ts`). */
  offers(placement: OfferPlacement) {
    return request<OfferDecision>(`/api/offers/${placement}`);
  },

  news(page = 1) {
    return request<NewsPage>(`/api/news?page=${page}`);
  },

  newsArticle(slug: string) {
    return request<NewsArticle>(`/api/news/${encodeURIComponent(slug)}`);
  },

  faq() {
    return request<FaqEntry[]>('/api/cms/faq');
  },
};
